<template>
  <!--
    The trial countdown in the header (Muhammad, 21 Sep 2026): Direct trial
    admins see how many days their free trial has left, and a way to buy.
    Reads the numeric days the API sends with the profile
    (trial_days_remaining, calendar days: 0 = ends today, negative =
    expired); falls back to the legacy "Your trial will expire in N days"
    sentence on an older API. Renders nothing for paid accounts, learners
    and the partner app. The day-before email (company:trial-expiry-reminders)
    uses the same arithmetic, so "1 day left" here is the day the email goes.

    The IMPROVE Live webinar button (6 Oct 2026) sits before Buy IMPROVE and
    opens the booking popup (WebinarBookingDialog). Since 9 Oct 2026 (Steve:
    it did not stand out) it is called IMPROVE Live and looks like Buy
    IMPROVE. Once booked it shows the date, and on the day "Join IMPROVE
    Live". It shows only while there is a booking or a date to book.

    A trial that has bought and is paying by bank transfer (10 Oct 2026)
    sees its invoice instead of the countdown and Buy IMPROVE: "Invoice sent:
    pay by 6 Nov" and View invoice (Stripe's page). Its trial stays open until
    the payment arrives; if the due date passes unpaid the account is
    suspended and this says so, in red.
  -->
  <div v-if="visible" class="trial-countdown" :class="{ 'is-urgent': urgent, 'is-expired': expired }" data-tour="trial-countdown">
    <v-icon small class="trial-countdown__icon">{{ icon }}</v-icon>
    <span class="trial-countdown__text">{{ label }}</span>
    <button
      v-if="webinarShown"
      type="button"
      class="trial-countdown__webinar"
      :title="webinarLabel"
      :aria-label="webinarLabel"
      @click="openWebinar"
    >
      <v-icon x-small class="trial-countdown__webinar-icon">{{ webinarIcon }}</v-icon>
      <span class="trial-countdown__webinar-text">{{ webinarLabel }}</span>
    </button>
    <a v-if="pendingInvoice" :href="pendingInvoice.url" target="_blank" rel="noopener" class="trial-countdown__buy">View invoice</a>
    <router-link v-else :to="buyRoute" class="trial-countdown__buy">Buy IMPROVE</router-link>
  </div>
</template>

<script>
import { canBookWebinar, currentBooking, openWebinarBooking, webinarAvailable } from "@/utils/webinarBooking";

// Today's date in the UK, Y-m-d: the webinar times are UK times.
function ukToday() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London" }).format(new Date());
}

export default {
  name: "TrialCountdown",
  computed: {
    webinarShown() {
      return canBookWebinar(this.$store.getters) && webinarAvailable();
    },
    webinarBooking() {
      return currentBooking();
    },
    webinarToday() {
      return !!this.webinarBooking && this.webinarBooking.date === ukToday();
    },
    webinarLabel() {
      if (!this.webinarBooking) return "IMPROVE Live";
      return this.webinarToday ? "Join IMPROVE Live" : "IMPROVE Live: " + this.webinarBooking.short_label;
    },
    webinarIcon() {
      if (!this.webinarBooking) return "mdi-calendar";
      return this.webinarToday ? "mdi-video" : "mdi-calendar-check";
    },
    days() {
      const n = this.$store.getters.trialDaysRemaining;
      if (typeof n === "number" && !isNaN(n)) return n;
      // Older API: the sentence only.
      const s = String(this.$store.getters.trialDays || "");
      if (/expire today/i.test(s)) return 0;
      if (/is expired/i.test(s)) return -1;
      const m = s.match(/(\d+)\s*day/);
      return m ? parseInt(m[1], 10) : null;
    },
    // The unpaid bank transfer invoice of a trial that has bought, or null.
    pendingInvoice() {
      return this.$store.getters.pendingInvoice;
    },
    // Suspended because that invoice went unpaid past its due date.
    suspended() {
      return !!this.pendingInvoice && Number(this.$store.getters.companyState) === 4;
    },
    visible() {
      if (this.$globalConfig.APP_NAME !== "direct") return false;
      if (Number(this.$store.getters.level) !== 2) return false;
      if (this.pendingInvoice) return true;
      const state = Number(this.$store.getters.companyState);
      return (state === 1 || state === 2) && this.days !== null;
    },
    expired() {
      if (this.pendingInvoice) return this.suspended;
      return this.days < 0 || Number(this.$store.getters.companyState) === 2;
    },
    urgent() {
      return !this.pendingInvoice && !this.expired && this.days <= 3;
    },
    icon() {
      if (this.expired) return "mdi-alert-circle";
      return this.pendingInvoice ? "mdi-file-document-outline" : "mdi-timer-sand";
    },
    // The in-app purchase page (/billing) is on staging only so far; where the
    // router does not know it, the Account status page carries the plan.
    buyRoute() {
      const r = this.$router.resolve("/billing");
      return r && r.route && r.route.matched.length ? "/billing" : "/admin/finance/account-status";
    },
    label() {
      if (this.suspended) return "Invoice overdue: account suspended";
      if (this.pendingInvoice) {
        const due = this.pendingInvoice.due_at;
        return due
          ? "Invoice sent: pay by " + new Date(due + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" })
          : "Invoice sent";
      }
      if (this.expired) return "Free trial ended";
      if (this.days === 0) return "Free trial ends today";
      if (this.days === 1) return "Free trial: 1 day left";
      return "Free trial: " + this.days + " days left";
    },
  },
  methods: {
    openWebinar() {
      openWebinarBooking();
    },
  },
};
</script>

<style scoped>
.trial-countdown {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-right: 12px;
  padding: 4px 6px 4px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-size: 13px;
  white-space: nowrap;
}

.trial-countdown__icon {
  color: #fff !important;
}

.trial-countdown__text {
  font-weight: 600;
}

/* Filled like Buy IMPROVE (same colours, padding and height). */
.trial-countdown__webinar {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border: 0;
  border-radius: 999px;
  background: #fff;
  color: #002458;
  font-weight: 700;
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s ease;
}

.trial-countdown__webinar:hover {
  background: #e6ecf5;
}

.trial-countdown__webinar:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.trial-countdown__webinar-icon {
  color: #002458 !important;
}

.trial-countdown__buy {
  padding: 3px 10px;
  border-radius: 999px;
  background: #fff;
  color: #002458;
  font-weight: 700;
  font-size: 12px;
  text-decoration: none;
  text-transform: none;
}

.trial-countdown.is-urgent {
  background: #c8641a;
}

.trial-countdown.is-expired {
  background: #b3261e;
}

@media (max-width: 959px) {
  .trial-countdown__text {
    display: none;
  }
  .trial-countdown {
    padding-left: 8px;
    margin-right: 6px;
  }
}

/* Phones: the webinar button keeps its icon; the label is its tooltip. */
@media (max-width: 599px) {
  .trial-countdown__webinar-text {
    display: none;
  }
  .trial-countdown__webinar {
    padding: 3px 7px;
  }
}
</style>
